import React, {useRef, useState, useEffect} from 'react';

import {useHistory} from 'react-router-dom';
import Icon from '../../../../components/AppIcon';
import styled from 'styled-components';
import {toast} from 'react-toastify';

// redux
import {useDispatch, useSelector} from 'react-redux';

// i18n
import {useTranslation} from 'react-i18next';

import ChapterFilePostProcessor from './chapterFilePostProcessor';
import {manuscriptFunctionUrl} from './manuscriptFunctionUrl';
import ux from '../../../../utils/uxAnalyticsUtil';
import authUtil from '../../../../utils/authUtil';
import featureToggleUtil from '../../../../utils/featureToggleUtil';

import {addChapters, createBookAndChapters} from '../../../../modules/book';
import {PostProcessingContextProvider} from './postProcessingContext';
import {finishImport} from './postImportNavigation';
import {useFeatureFlags} from '../../../../hooks/useFeatureFlags';
import {
  readSplitModePreference,
  writeSplitModePreference
} from './splitModePreference';

const UploadArea = styled.div`
  display: grid;
  justify-items: center;
  gap: 8px;
  padding: 24px;
  border: 2px dashed var(--br-border);
  border-radius: 10px;
  background: var(--br-raised-surface);
  color: var(--br-text);
  text-align: center;
  input { max-width: 100%; }
`;

const ProcessingOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
  background: rgba(0, 0, 0, 0.65);
  color: white;
  text-align: center;
`;

const ChapterFileUpload = ({
  uploadHeader,
  uploadMessage,

  onWaitingForFile = () => {},
  onClose = () => {},
  createBook
}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const toastId = useRef(null);
  const fileInputId = React.useId();

  const mountTimestamp = useRef(Date.now());
  const uploadMessageTimeout = useRef(0);

  const currentBook = useSelector(state => state.book.currentBook);
  const currentContentVersion = useSelector(
    state => state.book.currentContentVersion
  );
  const userProfile = useSelector(state => state.user.userProfile);
  const activeAccount = useSelector(state => state.account.activeAccount);

  const [uploadState, setUploadState] = useState();
  const [localUploadMessage, setLocalUploadMessage] = useState();
  const [documentData, setDocumentData] = useState();
  const [currentDocumentData, setCurrentDocumentData] = useState();
  // Whether the file currently being imported is Markdown (.md/.markdown).
  const [isMarkdownImport, setIsMarkdownImport] = useState(false);
  // Whether the file currently being imported is an EPUB.
  const [isEpubImport, setIsEpubImport] = useState(false);
  // Chapter-split mode for the current Markdown import, and the raw source we
  // keep so a change can be re-converted without asking for the file again.
  // `none` = "one file, one chapter": headings stay in the body as heading
  // text instead of becoming chapter breaks.
  //
  // Seeded from the author's last choice (localStorage) so a one-file-per-
  // chapter import does not mean re-picking the mode for every file. A first-
  // time author gets `auto`, i.e. today's behaviour.
  const [splitMode, setSplitMode] = useState(readSplitModePreference);
  const [splitModeBusy, setSplitModeBusy] = useState(false);
  const markdownSource = useRef(null);
  // Bumped on every (re-)conversion so the post-processing provider remounts on
  // the new document instead of holding the previous conversion's state.
  const [conversionSeq, setConversionSeq] = useState(0);

  // Markdown import is gated behind the `markdown_import` flag — the global GA
  // flag OR the caller's per-user beta grant, mirroring the server's
  // isFeatureEnabledForUser and the critique_groups UI gate. When off, the
  // dropzone neither advertises nor accepts .md files, and the API route rejects
  // markdownify with 403 FEATURE_DISABLED.
  const {flags} = useFeatureFlags();
  const markdownImportEnabled =
    flags?.markdown_import === true ||
    featureToggleUtil.hasFeatureAccess(userProfile, 'markdown_import');
  // Same two-tier gate for EPUB (`epub_import`).
  const epubImportEnabled =
    flags?.epub_import === true ||
    featureToggleUtil.hasFeatureAccess(userProfile, 'epub_import');

  // Whether this import targets a scene-structured (tiptap) book: a new book
  // when the author is in the scene_editor beta OR when importing Markdown or
  // EPUB (both always target tiptap, whose reader can render code blocks,
  // nested lists and tables), or an existing tiptap book we are appending
  // chapters to. Legacy Draft import is used for everyone else.
  const isTiptapImport = createBook
    ? isMarkdownImport ||
      isEpubImport ||
      featureToggleUtil.hasFeatureAccess(userProfile, 'scene_editor')
    : currentBook?.contentFormat === 'tiptap';

  useEffect(() => {
    mountTimestamp.current = Date.now();
    if (onWaitingForFile) {
      onWaitingForFile();
    }
    return () => {
      clearTimeout(uploadMessageTimeout.current);
    };
  }, []);

  const onProcessing = () => {
    setUploadState('PROCESSING');
    uploadMessageTimeout.current = setTimeout(() => {
      setLocalUploadMessage(t('ThisMightTakeAWhile'));
    }, 3000);
  };

  const handleUploadedDocumentData = documentData => {
    clearTimeout(uploadMessageTimeout.current);
    setDocumentData(documentData);
    setCurrentDocumentData(documentData);
    setConversionSeq(seq => seq + 1);
    setUploadState(undefined);
  };

  const convertManuscript = async file => {
    console.log('converting manuscript', file);
    const postData = new FormData();
    postData.append('file', file);
    try {
      const fileEnding = file.name.split('.').pop();
      const response = await fetch(
        manuscriptFunctionUrl(`/func/convert?format=${fileEnding}`),
        {
          method: 'POST',
          body: postData
        }
      );
      if (response.ok) {
        const chapters = await response.json();
        return chapters;
      }
      const {msg} = await response.json();
      toast.error(msg);
      throw new Error(msg);
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  // Markdown import: unlike DOCX/PDF (which go through the firebase `convert`
  // function), a .md file is read client-side and parsed + split into chapter
  // parts by the API (POST /chapters/markdownify). The response has the same
  // {title, parts:[{title, html, ...}]} shape as `convert`, so the
  // post-processor and per-part conversion below are unchanged. Markdown
  // imports always target the tiptap format (see isTiptapImport).
  const convertMarkdown = async (markdown, title, mode) => {
    try {
      const idToken = await authUtil.getFreshIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/chapters/markdownify`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'br-token': idToken
          },
          body: JSON.stringify({markdown, title, splitOn: mode || 'auto'})
        }
      );
      if (response.ok) {
        return await response.json();
      }
      const {err} = await response.json().catch(() => ({}));
      console.error('failed to convert markdown: status', response.status, err);
      toast.error(t('SomethingWentWrong'));
      return false;
    } catch (e) {
      console.error('failed to convert markdown', e);
      toast.error(t('SomethingWentWrong'));
      return false;
    }
  };

  // EPUB import: the file is binary, so unlike Markdown it is POSTed to the API
  // as multipart rather than read client-side. The API unzips it, walks the OPF
  // spine and returns the same {title, parts:[{title, html, ...}]} shape as
  // `convert`, so the post-processor and per-part conversion below are
  // unchanged. EPUB imports always target tiptap (see isTiptapImport).
  const convertEpub = async file => {
    try {
      const idToken = await authUtil.getFreshIdToken();
      const postData = new FormData();
      postData.append('file', file);
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/chapters/epubify`,
        {
          method: 'POST',
          headers: {'br-token': idToken},
          body: postData
        }
      );
      if (response.ok) {
        return await response.json();
      }
      const {err} = await response.json().catch(() => ({}));
      console.error('failed to convert epub: status', response.status, err);
      // these are user-actionable, so they get their own copy rather than the
      // generic "something went wrong"
      const messageByError = {
        drm_protected: t('EpubDrmProtected'),
        epub_too_large: t('EpubTooLarge'),
        invalid_epub: t('EpubInvalid'),
        no_content: t('EpubNoContent')
      };
      toast.error(messageByError[err] || t('SomethingWentWrong'));
      return false;
    } catch (e) {
      console.error('failed to convert epub', e);
      toast.error(t('SomethingWentWrong'));
      return false;
    }
  };

  const enrichPart = async (part, index) => {
    try {
      const response = await fetch(
        manuscriptFunctionUrl('/func/enrich'),
        {
          method: 'POST',
          body: JSON.stringify({
            ...part,
            title: part.title || `${t('Chapter')} ${index + 1}`
          })
        }
      );
      if (!response.ok) {
        // a non-2xx enrich response returns a JSON error body (e.g. {msg}),
        // which is truthy - guard against treating it as a valid part
        console.error('failed to enrich part: status', response.status);
        return false;
      }
      const enrichedPart = await response.json();
      // a successfully enriched part must carry draftified content; without it
      // the downstream save would create a broken/empty chapter
      if (!enrichedPart || typeof enrichedPart.content !== 'string') {
        console.error('enrich returned an unexpected payload', enrichedPart);
        return false;
      }
      return enrichedPart;
    } catch (e) {
      console.error('failed to enrich part', e);
      return false;
    }
  };

  // tiptap analog of enrichPart: convert the part's HTML to scene-structured
  // fields server-side (POST /api/v2/chapters/tiptapify) instead of draftifying
  // via the firebase enrich function. Returns {title, scenes, wordCount,
  // charCount, paragraphCount, beginning} — the fields the create/append routes
  // pass through onto the chapter.
  const tiptapifyPart = async (part, index, idToken) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/chapters/tiptapify`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'br-token': idToken
          },
          body: JSON.stringify({
            title: part.title || `${t('Chapter')} ${index + 1}`,
            html: part.html
          })
        }
      );
      if (!response.ok) {
        console.error('failed to tiptapify part: status', response.status);
        return false;
      }
      const scenePart = await response.json();
      // a valid scene part must carry a scenes[] array; without it the
      // downstream save would create a broken/empty chapter
      if (!scenePart || !Array.isArray(scenePart.scenes)) {
        console.error('tiptapify returned an unexpected payload', scenePart);
        return false;
      }
      return scenePart;
    } catch (e) {
      console.error('failed to tiptapify part', e);
      return false;
    }
  };

  // Re-run the Markdown conversion with another chapter-split mode. The raw
  // source is already in hand, so this is a single API round-trip and the
  // post-processor simply remounts on the new document.
  const changeSplitMode = async mode => {
    if (!markdownSource.current || mode === splitMode) {
      return;
    }
    ux.trackEvent({
      category: 'Imports',
      action: 'changed-markdown-split-mode',
      label: mode
    });
    setSplitModeBusy(true);
    const {markdown, title} = markdownSource.current;
    const newDocumentData = await convertMarkdown(markdown, title, mode);
    // The author can cancel the whole import while this is in flight. This
    // component is not always unmounted on cancel (createFirstBookSegment
    // renders it with no onClose), so without this guard the late response
    // would re-open the post-processor on the file they just dismissed.
    // `cancelUpload` clears the source, which is what we check.
    if (!markdownSource.current) {
      return;
    }
    setSplitModeBusy(false);
    if (!newDocumentData) {
      // convertMarkdown already surfaced the error; keep the current document
      // rather than dropping the author out of the import.
      return;
    }
    setSplitMode(mode);
    // Only remember a mode that actually produced a document, so a failed
    // conversion never becomes the default for the next file.
    writeSplitModePreference(mode);
    handleUploadedDocumentData(newDocumentData);
  };

  const cancelUpload = () => {
    const timeFromMountToCancel = mountTimestamp.current - Date.now();
    setDocumentData(undefined);
    setCurrentDocumentData(undefined);
    setIsMarkdownImport(false);
    // Back to the remembered mode, not a hardcoded `auto` — cancelling one
    // import must not silently undo the author's standing choice.
    setSplitMode(readSplitModePreference());
    setSplitModeBusy(false);
    markdownSource.current = null;
    ux.timing({
      category: ux.categories.UPLOAD,
      variable: 'mount-to-cancel',
      value: timeFromMountToCancel, // in milliseconds
      label: 'Upload'
    });
    ux.trackEvent({
      category: ux.categories.UPLOAD,
      action: 'Cancelled upload'
    });
    if (onClose) {
      onClose();
    }
  };

  const submitParts = async () => {
    ux.trackEvent({
      category: 'Imports',
      action: 'clicked-complete-upload'
    });
    const timeFromMountToUpload = mountTimestamp.current - Date.now();
    ux.timing({
      category: ux.categories.UPLOAD,
      variable: 'mount-to-upload',
      value: parseInt(timeFromMountToUpload, 10), // in milliseconds
      label: 'Upload'
    });

    toastId.current = toast.info(t('ImportStarted'), {autoClose: false});
    onProcessing();
    submitPart(
      {
        parts: currentDocumentData.parts,
        currentIndex: 0,
        book: createBook ? null : currentBook,
        contentVersionId: createBook ? null : currentContentVersion._id
      },
      function done({book, contentVersionId, partsAdded}) {
        toast.update(toastId.current, {
          render: t('Done'),
          type: toast.TYPE.INFO,
          autoClose: 5000
        });
        // Dismiss the importer, then land the author somewhere useful:
        // the chapter overview of the version we imported into for an existing
        // book, the book overview for a brand new one. The previous
        // `switch (createBook) { case false: ... }` never matched the
        // existing-book path, because partsList renders ChapterUploadModal
        // without the prop - `createBook` is `undefined` there, so it fell
        // through to `default:` and pushed the author out to the book overview.
        finishImport({
          createBook,
          book,
          contentVersionId,
          onClose,
          push: route => history.push(route)
        });
      }
    );
  };

  // Surface an import failure instead of leaving the progress toast spinning
  // forever. Without this, any failed network call in the recursive chain below
  // froze the importer on "Importing chapter X of Y".
  const handleImportError = (error, currentIndex, totalParts) => {
    console.error('manuscript import failed', error);
    const detail = error && error.message ? error.message : t('SomethingWentWrong');
    toast.update(toastId.current, {
      render: t('ImportFailedAtChapterX', {
        _x: currentIndex + 1,
        _y: totalParts,
        _error: detail
      }),
      type: toast.TYPE.ERROR,
      autoClose: 8000
    });
    setUploadState(undefined);
    ux.trackEvent({
      category: 'Imports',
      action: 'import-failed',
      label: detail
    });
  };

  const submitPart = async (args, done) => {
    try {
      await submitPartWithoutErrorBoundary(args, done);
    } catch (error) {
      handleImportError(
        error,
        args.currentIndex,
        args.parts?.length ?? 0
      );
    }
  };

  const submitPartWithoutErrorBoundary = async (
    {parts, currentIndex, book, contentVersionId},
    done
  ) => {
    // get enriched part data
    const currentPart = parts[currentIndex];
    const idToken = await authUtil.getFreshIdToken();
    if (!currentPart) {
      if (!!book && !!contentVersionId) {
        done({
          book,
          contentVersionId,
          partsAdded: currentIndex
        });
      }
      return;
    }
    toast.update(toastId.current, {
      render: t('ImportingChapterXofY', {
        _x: currentIndex + 1,
        _y: parts?.length ?? 0
      }),
      type: toast.TYPE.INFO
    });
    const enrichedPart = isTiptapImport
      ? await tiptapifyPart(currentPart, currentIndex, idToken)
      : await enrichPart(currentPart, currentIndex);
    if (!enrichedPart) {
      // failed to convert the part - abort here and stop the spinner
      handleImportError(
        new Error(t('SomethingWentWrong')),
        currentIndex,
        parts?.length ?? 0
      );
      return;
    }
    // succeeded with the converted part, proceed to creating book or appending chapters
    if (currentIndex === 0 && book === null) {
      // 1st chapter and no book - create book
      const bookData = {
        ...currentDocumentData,
        parts: [enrichedPart],
        // new books created by a scene_editor-beta author import in tiptap format
        ...(isTiptapImport ? {contentFormat: 'tiptap'} : {})
      };
      const accountId = activeAccount?._id || userProfile.account?._id;
      // Create book and chapters
      dispatch(
        createBookAndChapters(
          idToken,
          {
            ...bookData,
            // Omitting accountId lets POST /books provision a missing personal
            // account. Sending undefined is avoided so the request payload is
            // unambiguous to every client/transport layer.
            ...(accountId ? {accountId} : {})
          },
          createdBook => {
            // guard against a malformed response so we don't throw (and hang)
            // while trying to read the new content version id
            const newContentVersionId =
              createdBook &&
              Array.isArray(createdBook.content) &&
              createdBook.content[0] &&
              createdBook.content[0]._id;
            if (!newContentVersionId) {
              handleImportError(
                new Error(t('SomethingWentWrong')),
                currentIndex,
                parts?.length ?? 0
              );
              return;
            }
            // move on to submit the next part
            submitPart(
              {
                parts,
                currentIndex: currentIndex + 1,
                book: createdBook,
                contentVersionId: newContentVersionId
              },
              done
            );
          },
          error => handleImportError(error, currentIndex, parts?.length ?? 0)
        )
      );
    } else {
      // a book already exists, append chapters to it instead of creating a new one
      dispatch(
        addChapters(
          idToken,
          book._id,
          contentVersionId,
          [enrichedPart],
          newChapters => {
            // move on to submit the next part
            submitPart(
              {
                parts,
                currentIndex: currentIndex + 1,
                book,
                contentVersionId
              },
              done
            );
          },
          error => handleImportError(error, currentIndex, parts?.length ?? 0)
        )
      );
    }
  };

  const acceptedExtensions = [
    'docx', 'pdf',
    ...(markdownImportEnabled ? ['md', 'markdown'] : []),
    ...(epubImportEnabled ? ['epub'] : [])
  ];
  const accept = acceptedExtensions.map(extension => `.${extension}`).join(',');

  const handleFile = async file => {
    if (!file) return;
    const fileEnding = (file.name.split('.').pop() || '').toLowerCase();
    if (!acceptedExtensions.includes(fileEnding)) {
      toast.error(t('UnsupportedFileType'));
      return;
    }
    onProcessing();
    const isMd = markdownImportEnabled && ['md', 'markdown'].includes(fileEnding);
    const isEpub = epubImportEnabled && fileEnding === 'epub';
    setIsMarkdownImport(isMd);
    setIsEpubImport(isEpub);
    const rememberedMode = readSplitModePreference();
    setSplitMode(rememberedMode);

    if (isEpub && !createBook && currentBook?.contentFormat !== 'tiptap') {
      toast.error(t('EpubNeedsSceneBook'));
      clearTimeout(uploadMessageTimeout.current);
      setUploadState(undefined);
      return;
    }

    let convertedDocument;
    if (isEpub) {
      markdownSource.current = null;
      convertedDocument = await convertEpub(file);
    } else if (isMd) {
      const markdown = await file.text();
      const title = file.name.replace(/\.(md|markdown)$/i, '');
      markdownSource.current = {markdown, title};
      convertedDocument = await convertMarkdown(markdown, title, rememberedMode);
    } else {
      markdownSource.current = null;
      convertedDocument = await convertManuscript(file);
    }
    handleUploadedDocumentData(convertedDocument);
  };

  return (
    <div>
      <UploadArea
        data-testid="chapter-upload-dropzone"
        onDragOver={event => event.preventDefault()}
        onDrop={event => {
          event.preventDefault();
          handleFile(event.dataTransfer.files?.[0]);
        }}
      >
        <Icon name="arrow up" size="large" />
        <label htmlFor={fileInputId}>{uploadHeader || t('ImportManuscript')}</label>
        <input
          id={fileInputId}
          aria-label={t('ChooseFile')}
          type="file"
          accept={accept}
          onChange={event => {
            handleFile(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
        {(localUploadMessage || uploadMessage) && (
          <p>{localUploadMessage || uploadMessage}</p>
        )}
      </UploadArea>
      {
        // we are uploading - block the background elements
        uploadState === 'PROCESSING' && (
          <ProcessingOverlay role="status" aria-live="polite">
            <div>
              <Icon name='spinner' loading />
              <h2>{t('Processing')}</h2>
              {(localUploadMessage || uploadMessage) && (
                <p style={{fontSize: '0.6em'}}>{t('ThisMightTakeAWhile')}</p>
              )}
            </div>
          </ProcessingOverlay>
        )
      }
      {
        // received the document data. switch to post processor.
        documentData && (
          <PostProcessingContextProvider
            key={conversionSeq}
            initialValue={documentData}
            onUpdate={newDocumentData =>
              setCurrentDocumentData(newDocumentData)
            }>
            <ChapterFilePostProcessor
              documentData={documentData}
              onCancelClicked={cancelUpload}
              onCompleteClicked={submitParts}
              {...(isMarkdownImport
                ? {
                    splitMode,
                    splitModeBusy,
                    onSplitModeChange: changeSplitMode
                  }
                : {})}
            />
          </PostProcessingContextProvider>
        )
      }
    </div>
  );
};

export default ChapterFileUpload;
